import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Loader2, Copy, Play, Square, RotateCcw, Clock, Film, Clapperboard,
  Settings, Server, History, MessageCircle, Send, CheckCircle2, AlertTriangle,
  Wand2, Trash2, Wrench, Plus,
} from "lucide-react";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEta = (s) => {
  if (s === -1 || s === null || s === undefined) return "calculando…";
  if (s < 1) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return [h && `${h}h`, m && `${m}min`, `${sec}s`].filter(Boolean).join(" ");
};

const fmtClock = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  return h ? `${p(h)}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
};

const LogBox = ({ lines }) => (
  <div className="mt-3 h-56 overflow-auto rounded-lg bg-slate-900 p-3 font-mono-code text-[11px] leading-relaxed text-green-300">
    {lines?.length ? lines.map((l, i) => <div key={i}>{l}</div>) : <span className="text-slate-500">Aguardando…</span>}
  </div>
);

const Progress = ({ p, t }) => (
  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
    <div className="h-full rounded-full bg-green-500 transition-all duration-500" style={{ width: `${t ? Math.min(100, (p / t) * 100) : 0}%` }} />
  </div>
);

const Stat = ({ label, value, color }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
    <div className={`font-display text-lg font-bold ${color}`} data-testid={`stat-${label}`}>{(value || 0).toLocaleString("pt-BR")}</div>
    <div className="text-[11px] font-medium text-slate-500">{label}</div>
  </div>
);

export default function Step5Sync({ code, data, goStep }) {
  const [s, setS] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [hour, setHour] = useState(data?.sync_auto?.hour || "03:00");
  const [autoOn, setAutoOn] = useState(!!data?.sync_auto?.enabled);
  const pollRef = useRef(null);
  const startedRef = useRef(false);
  const t = useT();
  const [clock, setClock] = useState(0);
  const [bf, setBf] = useState(null);
  const [cl, setCl] = useState(null);
  const bfRef = useRef(null);
  const clRef = useRef(null);
  const [pc, setPc] = useState(null);
  const pcRef = useRef(null);
  const [rp, setRp] = useState(null);
  const rpRef = useRef(null);
  const [rpScope, setRpScope] = useState("all");
  const [dnsList, setDnsList] = useState([""]);
  const [pcScope, setPcScope] = useState("all");
  const [delOff, setDelOff] = useState(false);
  const [off, setOff] = useState(null);
  const offRef = useRef(null);

  const pollOffline = () => {
    clearInterval(offRef.current);
    offRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/panel/${code}/offline/delete/status`);
        setOff(data);
        if (data.status !== "running") { clearInterval(offRef.current); load(); }
      } catch (_) {}
    }, 1500);
  };
  const startDeleteAll = async () => {
    try {
      const { data } = await axios.post(`${API}/panel/${code}/offline/delete`, { all: true });
      if (data.job) { setOff({ status: "running", removed: 0, scanned: 0, total: 0, percent: 0, log: ["Iniciando…"] }); pollOffline(); }
      else { toast.success(`Cópias antigas apagadas (${data.removed} removido(s))`); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao apagar."); }
  };
  const stopOffline = async () => { try { await axios.post(`${API}/panel/${code}/offline/delete/stop`); toast.success("Parando…"); } catch (_) { toast.error("Falha ao parar."); } };

  const deleteOffline = async (payload, msg) => {
    setDelOff(true);
    try {
      const { data } = await axios.post(`${API}/panel/${code}/offline/delete`, payload);
      toast.success(`${msg} (${data.removed} removido(s) do servidor)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao apagar."); }
    finally { setDelOff(false); }
  };

  const load = async () => {
    try {
      const { data: r } = await axios.get(`${API}/panel/${code}/sync/status`);
      if (r.status === "idle" && !startedRef.current) {
        startedRef.current = true;
        await axios.post(`${API}/panel/${code}/sync/start`);
        return load();
      }
      setS(r);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 1500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/panel/${code}/offline/delete/status`).then(({ data }) => {
      if (alive && data && data.status !== "idle") { setOff(data); if (data.status === "running") pollOffline(); }
    }).catch(() => {});
    return () => { alive = false; clearInterval(offRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Relógio ao vivo: reseta com o valor do servidor e incrementa a cada 1s.
  useEffect(() => { setClock(s?.elapsed_seconds || 0); }, [s?.elapsed_seconds]);
  useEffect(() => {
    if (s?.status !== "running") return;
    const id = setInterval(() => setClock((c) => c + 1), 1000);
    return () => clearInterval(id);
  }, [s?.status]);

  const act = async (path, msg) => {
    try { await axios.post(`${API}/panel/${code}/sync/${path}`); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha na ação."); }
  };
  const copy = (t, m) => { navigator.clipboard.writeText(t || ""); toast.success(m); };

  const pollBackfill = () => {
    clearInterval(bfRef.current);
    bfRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/panel/${code}/backfill/status`);
        setBf(data);
        if (data.status !== "running") clearInterval(bfRef.current);
      } catch (_) {}
    }, 2000);
  };
  const startBackfill = async () => {
    try {
      await axios.post(`${API}/panel/${code}/backfill/start`);
      toast.success("Backfill iniciado! Completando dados do TMDB…");
      setBf({ status: "running", scanned: 0, updated: 0, total: 0, log: ["Iniciando…"] });
      pollBackfill();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao iniciar o backfill."); }
  };

  const pollCleanup = () => {
    clearInterval(clRef.current);
    clRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/panel/${code}/cleanup/duplicates/status`);
        setCl(data);
        if (data.status !== "running") clearInterval(clRef.current);
      } catch (_) {}
    }, 2000);
  };
  const startPreview = async () => {
    try {
      await axios.post(`${API}/panel/${code}/cleanup/duplicates/start`, { preview: true });
      toast.success("Analisando duplicados…");
      setCl({ status: "running", mode: "preview", movies: 0, series: 0, episodes: 0, log: ["Analisando…"] });
      pollCleanup();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao analisar."); }
  };
  const confirmCleanup = async () => {
    try {
      await axios.post(`${API}/panel/${code}/cleanup/duplicates/start`, { preview: false });
      toast.success("Removendo duplicados…");
      setCl({ status: "running", mode: "delete", movies: 0, series: 0, episodes: 0, log: ["Removendo…"] });
      pollCleanup();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao remover."); }
  };

  const pollProvider = () => {
    clearInterval(pcRef.current);
    pcRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/panel/${code}/provider-cleanup/status`);
        setPc(data);
        if (data.status !== "running") clearInterval(pcRef.current);
      } catch (_) {}
    }, 2000);
  };
  const startProvider = async () => {
    const dns = dnsList.map((d) => d.trim()).filter(Boolean);
    if (!dns.length) { toast.error("Informe pelo menos um DNS/fornecedor."); return; }
    try {
      await axios.post(`${API}/panel/${code}/provider-cleanup/start`, { dns, scope: pcScope });
      toast.success("Remoção por fornecedor iniciada…");
      setPc({ status: "running", scanned: 0, total: 0, movies: 0, episodes: 0, series: 0, percent: 0, log: ["Iniciando…"] });
      setDnsList([""]);
      pollProvider();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao iniciar."); }
  };
  const stopProvider = async () => { try { await axios.post(`${API}/panel/${code}/provider-cleanup/stop`); toast.success("Parando…"); } catch (e) { toast.error("Falha ao parar."); } };

  const pollRepair = () => {
    clearInterval(rpRef.current);
    rpRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/panel/${code}/link-repair/status`);
        setRp(data);
        if (data.status !== "running") { clearInterval(rpRef.current); load(); }
      } catch (_) {}
    }, 2500);
  };
  const startRepair = async () => {
    try {
      await axios.post(`${API}/panel/${code}/link-repair/start`, { scope: rpScope });
      toast.success("Reparo de links iniciado…");
      setRp({ status: "running", scanned: 0, total: 0, repaired: 0, offline: 0, percent: 0, log: ["Iniciando…"] });
      pollRepair();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao iniciar o reparo."); }
  };
  const stopRepair = async () => { try { await axios.post(`${API}/panel/${code}/link-repair/stop`); toast.success("Parando…"); } catch (e) { toast.error("Falha ao parar."); } };

  // Ao montar (ou reabrir o link): rehidrata o estado de backfill/limpeza do
  // servidor e retoma o polling se algum job ainda estiver rodando — assim um
  // backfill em andamento ou uma prévia aguardando confirmação sobrevive ao F5.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/panel/${code}/backfill/status`);
        if (alive && data && data.status !== "idle") { setBf(data); if (data.status === "running") pollBackfill(); }
      } catch (_) {}
      try {
        const { data } = await axios.get(`${API}/panel/${code}/cleanup/duplicates/status`);
        if (alive && data && data.status !== "idle") { setCl(data); if (data.status === "running") pollCleanup(); }
      } catch (_) {}
      try {
        const { data } = await axios.get(`${API}/panel/${code}/provider-cleanup/status`);
        if (alive && data && data.status !== "idle") { setPc(data); if (data.status === "running") pollProvider(); }
      } catch (_) {}
      try {
        const { data } = await axios.get(`${API}/panel/${code}/link-repair/status`);
        if (alive && data && data.status !== "idle") { setRp(data); if (data.status === "running") pollRepair(); }
      } catch (_) {}
    })();
    return () => { alive = false; clearInterval(bfRef.current); clearInterval(clRef.current); clearInterval(pcRef.current); clearInterval(rpRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const saveAuto = async () => {
    try {
      const { data: r } = await axios.post(`${API}/panel/${code}/sync/auto`, { enabled: !autoOn, hour });
      setAutoOn(r.enabled); toast.success(r.enabled ? `Sincronia automática ativada às ${r.hour}` : "Sincronia automática desativada");
    } catch (_) { toast.error("Falha ao salvar."); }
  };

  if (!s) return <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-green-600" /></div>;
  const stopped = s.status === "stopped";
  const done = s.status === "done";
  const syncBusy = !stopped && !done;
  const maintBusy = bf?.status === "running" || cl?.status === "running" || pc?.status === "running" || rp?.status === "running" || off?.status === "running";
  const stopBackfill = async () => { try { await axios.post(`${API}/panel/${code}/backfill/stop`); toast.success("Parando…"); } catch (e) { toast.error("Falha ao parar."); } };
  const stopCleanup = async () => { try { await axios.post(`${API}/panel/${code}/cleanup/duplicates/stop`); toast.success("Parando…"); } catch (e) { toast.error("Falha ao parar."); } };
  const totProc = (s.movies?.processed || 0) + (s.series?.processed || 0);
  const totTot = (s.movies?.total || 0) + (s.series?.total || 0);
  const pct = totTot ? Math.min(100, Math.round((totProc / totTot) * 100)) : (done ? 100 : 0);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-6" data-testid="panel-step5">
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        O script usa várias técnicas para inserir no menor tempo possível — a velocidade depende da rede, do seu MAIN e da lista. Evite usar quando muita gente estiver conectada. Não precisa deixar o navegador aberto; salve o link desta página para acompanhar depois.
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
        {done ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : stopped ? <AlertTriangle className="h-5 w-5 text-red-500" /> : <Loader2 className="h-5 w-5 animate-spin text-green-600" />}
        <span className="font-medium text-slate-700">
          {done ? "Sincronização concluída!" : stopped ? "Sincronização parada." : "Sincronizando…"}
        </span>
        <span data-testid="sync-scope-label" className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
          {s.scope === "movies" ? "Somente Filmes" : s.scope === "series" ? "Somente Séries" : "Filmes e Séries"}
        </span>
        <span className="ml-auto flex items-center gap-3 text-xs font-semibold text-slate-500">
          <span className="flex items-center gap-1" data-testid="sync-clock"><Clock className="h-3.5 w-3.5" /> {fmtClock(clock)}</span>
          <span data-testid="sync-percent" className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">{pct}%</span>
          <span data-testid="sync-processed" className="hidden sm:inline">{totProc.toLocaleString("pt-BR")}/{totTot.toLocaleString("pt-BR")}</span>
        </span>
      </div>

      {stopped && s.error && (
        <div data-testid="sync-error" className="flex items-start gap-2 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>A sincronização foi interrompida.</b>
            <span className="mt-1 block">{s.error}</span>
            <span className="mt-1 block text-[12px] text-red-600">Ajuste a conexão e clique em <b>Reiniciar Sincronia</b> abaixo.</span>
          </div>
        </div>
      )}

      {/* Barra de progresso global em tempo real */}
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4" data-testid="sync-global">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-display font-bold text-slate-800">{t("steps.s5progress")}</span>
          <span className="text-xs text-slate-500">{t("steps.s5remaining")} {Math.max(0, totTot - totProc).toLocaleString("pt-BR")} · ETA {fmtEta(s.eta_seconds)}</span>
        </div>
        <Progress p={totProc} t={totTot} />
      </div>

      {/* Mensagens */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[["WhatsApp", MessageCircle, "bg-green-600", s.whatsapp_text, "wpp"], ["Telegram", Send, "bg-sky-500", s.telegram_text, "tg"]].map(([appName, Ic, bg, txt, id]) => (
          <div key={id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`msg-${id}`}>
            <div className={`flex items-center gap-2 px-5 py-3 font-display text-lg font-bold text-white ${bg}`}><Ic className="h-5 w-5" /> {appName}</div>
            <div className="p-4">
              <Button size="sm" data-testid={`copy-${id}`} onClick={() => copy(txt, t("steps.s5msgCopied"))} className={`${bg} text-white`}>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("steps.s5copy")}
              </Button>
              <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs text-slate-700">{txt || "—"}</pre>
            </div>
          </div>
        ))}
      </div>

      {/* Controles */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          <Button data-testid="sync-auto-toggle" onClick={() => setAutoOpen(!autoOpen)}
            className={`${autoOn ? "bg-green-700" : "bg-green-600"} text-white hover:bg-green-700`}>
            <Play className="mr-2 h-4 w-4" /> {autoOn ? `${t("steps.s5autoOn")}: ${hour}` : t("steps.s5autoOff")}
          </Button>
          <Button data-testid="sync-stop" onClick={() => act("stop", "Sincronização parada.")} disabled={stopped || done || maintBusy}
            className="bg-red-500 text-white hover:bg-red-600"><Square className="mr-2 h-4 w-4" /> {t("steps.s5stop")}</Button>
          <Button data-testid="sync-restart" onClick={() => { startedRef.current = true; act("restart", "Sincronização reiniciada do zero."); }} disabled={maintBusy}
            className="bg-rose-600 text-white hover:bg-rose-700"><RotateCcw className="mr-2 h-4 w-4" /> {t("steps.s5restart")}</Button>
          {maintBusy && <span className="self-center text-xs text-amber-600" data-testid="sync-locked-note">⚠️ Aguarde a manutenção terminar para parar/reiniciar.</span>}
        </div>
        {autoOpen && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="auto-panel">
            <span className="text-sm text-slate-600">{t("steps.s5every")}</span>
            <input type="time" data-testid="auto-hour" value={hour} onChange={(e) => setHour(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
            <Button size="sm" data-testid="auto-save" onClick={saveAuto} className="bg-green-600 text-white hover:bg-green-700">
              {autoOn ? t("steps.s5deact") : t("steps.s5saveAct")}
            </Button>
          </div>
        )}
        {stopped && (
          <div className="flex flex-wrap gap-3" data-testid="stopped-actions">
            <Button variant="outline" data-testid="alterar-config" onClick={() => goStep(2)}><Settings className="mr-2 h-4 w-4" /> {t("steps.s5chgConfig")}</Button>
            <Button variant="outline" data-testid="alterar-sistema" onClick={() => goStep(1)}><Server className="mr-2 h-4 w-4" /> {t("steps.s5chgSystem")}</Button>
          </div>
        )}
      </div>

      {/* Ferramentas de manutenção */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="maintenance-tools">
        <div className="flex items-center gap-2 bg-slate-800 px-5 py-3 font-display text-lg font-bold text-white"><Wrench className="h-5 w-5" /> {t("steps.s5maint")}</div>
        {syncBusy && (
          <div data-testid="maint-locked-banner" className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-5 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" /> A sincronização está em andamento. Pare ou aguarde ela terminar para usar as ferramentas de manutenção.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
          {/* Backfill de Filmes e Séries */}
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex items-center gap-2 font-semibold text-indigo-800"><Wand2 className="h-4 w-4" /> Completar dados do TMDB</div>
            <p className="mt-1 text-xs text-indigo-700">Preenche capa, sinopse, gênero e ano dos <b>filmes e séries</b> antigos que ainda estão incompletos — sem re-sincronizar a lista.</p>
            <Button size="sm" data-testid="backfill-start" onClick={startBackfill} disabled={syncBusy || maintBusy}
              className="mt-3 bg-indigo-600 text-white hover:bg-indigo-700">
              {bf?.status === "running" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
              {bf?.status === "running" ? "Completando…" : "Completar dados agora"}
            </Button>
            {bf?.status === "running" && (
              <Button size="sm" variant="outline" data-testid="backfill-stop" onClick={stopBackfill}
                className="ml-2 border-red-300 text-red-600 hover:bg-red-50"><Square className="mr-1.5 h-3.5 w-3.5" /> Parar</Button>
            )}
            {bf && bf.status !== "idle" && (
              <div className="mt-3 text-xs" data-testid="backfill-status">
                <div className="mb-1 flex justify-between text-indigo-800">
                  <span>🎬 {bf.updated_movies || 0} filmes · 📺 {bf.updated_series || 0} séries · {bf.scanned || 0}/{bf.total || 0} verificados</span>
                  <span>{bf.percent || 0}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-indigo-200">
                  <div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${bf.percent || 0}%` }} />
                </div>
                {bf.phase === "series" && bf.status === "running" && <p className="mt-1 text-indigo-600">Completando séries…</p>}
                {bf.error && <p className="mt-1 text-red-600">{bf.error}</p>}
                {bf.status === "done" && <p className="mt-1 font-semibold text-green-700">✅ {(bf.log || []).slice(-1)[0]}</p>}
              </div>
            )}
          </div>
          {/* Limpar duplicados (prévia → confirmar) */}
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
            <div className="flex items-center gap-2 font-semibold text-rose-800"><Trash2 className="h-4 w-4" /> Analisar e limpar duplicados</div>
            <p className="mt-1 text-xs text-rose-700">Analise primeiro quantos duplicados existem e confirme para remover (filmes, séries e episódios) — sem re-sincronizar.</p>
            <Button size="sm" data-testid="cleanup-dupes-start" onClick={startPreview} disabled={syncBusy || maintBusy}
              className="mt-3 bg-rose-600 text-white hover:bg-rose-700">
              {cl?.status === "running" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              {cl?.status === "running" ? (cl?.mode === "preview" ? "Analisando…" : "Removendo…") : "Analisar duplicados"}
            </Button>
            {cl?.status === "running" && (
              <Button size="sm" variant="outline" data-testid="cleanup-stop" onClick={stopCleanup}
                className="ml-2 border-red-300 text-red-600 hover:bg-red-50"><Square className="mr-1.5 h-3.5 w-3.5" /> Parar</Button>
            )}
            {cl && cl.status !== "idle" && (
              <div className="mt-3 text-xs" data-testid="cleanup-status">
                <div className="flex flex-wrap gap-x-3 text-rose-800">
                  <span>🎬 {cl.movies || 0} filmes</span><span>📺 {cl.series || 0} séries</span><span>🎞️ {cl.episodes || 0} eps</span>
                </div>
                {(cl.log || []).slice(-1)[0] && <p className="mt-1 text-rose-700">{(cl.log || []).slice(-1)[0]}</p>}
                {cl.error && <p className="mt-1 text-red-600">{cl.error}</p>}
                {cl.status === "done" && cl.mode === "preview" && ((cl.movies || 0) + (cl.series || 0) + (cl.episodes || 0) > 0) && (
                  <Button size="sm" data-testid="cleanup-confirm" onClick={confirmCleanup} disabled={syncBusy}
                    className="mt-2 bg-red-700 text-white hover:bg-red-800">
                    <Trash2 className="mr-2 h-4 w-4" /> Confirmar e remover {(cl.movies || 0) + (cl.series || 0) + (cl.episodes || 0)} duplicado(s)
                  </Button>
                )}
                {cl.status === "done" && cl.mode === "preview" && ((cl.movies || 0) + (cl.series || 0) + (cl.episodes || 0) === 0) && (
                  <p className="mt-1 font-semibold text-green-700">✅ Nenhum duplicado encontrado!</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Remover conteúdo por fornecedor (DNS) */}
        <div className="border-t border-slate-100 p-5" data-testid="provider-cleanup-card">
          <div className="flex items-center gap-2 font-semibold text-slate-800"><Server className="h-4 w-4 text-orange-600" /> Remover conteúdo por fornecedor (DNS)</div>
          <p className="mt-1 text-xs text-slate-500">Informe um ou mais DNS/hosts de fornecedor e remova o conteúdo que veio deles. Escolha remover tudo, ou só filmes, só séries, ou só episódios. As séries que ficarem sem episódios são apagadas.</p>
          <div className="mt-3 space-y-2">
            {dnsList.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <input data-testid={`pc-dns-${i}`} value={d} disabled={pc?.status === "running"}
                  onChange={(e) => setDnsList(dnsList.map((x, idx) => (idx === i ? e.target.value : x)))}
                  placeholder="ex: dns.fornecedor.com ou 191.101.72.45:8080"
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono-code text-xs focus:border-orange-500 focus:outline-none disabled:bg-slate-100" />
                {dnsList.length > 1 && (
                  <Button size="icon" variant="outline" data-testid={`pc-dns-del-${i}`} disabled={pc?.status === "running"}
                    onClick={() => setDnsList(dnsList.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid="pc-dns-add" disabled={pc?.status === "running"}
              onClick={() => setDnsList([...dnsList, ""])} className="text-orange-700"><Plus className="mr-1.5 h-4 w-4" /> Adicionar outro DNS</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="pc-scope">
            {[["all", "Tudo"], ["movies", "Só filmes"], ["series", "Só séries"], ["episodes", "Só episódios"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`pc-scope-${v}`} disabled={pc?.status === "running"}
                onClick={() => setPcScope(v)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 ${pcScope === v ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-orange-300"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" data-testid="provider-cleanup-start" onClick={startProvider} disabled={syncBusy || maintBusy}
              className="bg-orange-600 text-white hover:bg-orange-700">
              {pc?.status === "running" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              {pc?.status === "running" ? "Removendo…" : "Iniciar remoção"}
            </Button>
            {pc?.status === "running" && (
              <Button size="sm" variant="outline" data-testid="provider-cleanup-stop" onClick={stopProvider}
                className="border-red-300 text-red-600 hover:bg-red-50"><Square className="mr-1.5 h-3.5 w-3.5" /> Parar</Button>
            )}
          </div>
          {pc && pc.status !== "idle" && (
            <div className="mt-3 text-xs" data-testid="provider-cleanup-status">
              <div className="mb-1 flex justify-between text-slate-700">
                <span>🎬 {pc.movies || 0} filmes · 🎞️ {pc.episodes || 0} eps · 📺 {pc.series || 0} séries · {pc.scanned || 0}/{pc.total || 0}</span>
                <span>{pc.percent || 0}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-orange-200">
                <div className="h-full rounded-full bg-orange-600 transition-all" style={{ width: `${pc.percent || 0}%` }} />
              </div>
              {pc.error && <p className="mt-1 text-red-600">{pc.error}</p>}
              {pc.status === "done" ? <p className="mt-1 font-semibold text-green-700">✅ {(pc.log || []).slice(-1)[0]}</p>
                : (pc.log || []).slice(-1)[0] && <p className="mt-1 text-slate-600">{(pc.log || []).slice(-1)[0]}</p>}
            </div>
          )}
        </div>
      </section>

      {/* Consertar links quebrados (reparo in-place) */}
      <section className="overflow-hidden rounded-2xl border border-teal-200 bg-white" data-testid="repair-section">
        <div className="flex items-center gap-2 bg-teal-600 px-5 py-3 font-display text-lg font-bold text-white"><Wrench className="h-5 w-5" /> Consertar links quebrados (trocar por reserva)</div>
        <div className="p-5">
          <p className="text-xs text-slate-500">Varre o conteúdo que <b>já está no seu painel</b>, testa cada link e, se estiver caído, <b>troca pelo mesmo conteúdo de um fornecedor reserva</b> (sem apagar, sem re-subir tudo). O que não tiver substituto vai para "Conteúdo Offline". Use os fornecedores da Etapa 3 (ordem = prioridade).</p>
          {syncBusy && <p className="mt-2 text-xs text-amber-600">⚠️ Pare/aguarde a sincronização para usar esta ferramenta.</p>}
          {!syncBusy && maintBusy && rp?.status !== "running" && <p className="mt-2 text-xs text-amber-600">⚠️ Aguarde a outra manutenção terminar.</p>}
          <div className="mt-3 flex flex-wrap gap-2" data-testid="repair-scope">
            {[["all", "Tudo"], ["movies", "Só filmes"], ["series", "Só séries"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`repair-scope-${v}`} disabled={syncBusy || maintBusy}
                onClick={() => setRpScope(v)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${rpScope === v ? "border-teal-500 bg-teal-500 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-teal-300"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" data-testid="repair-start" onClick={startRepair} disabled={syncBusy || maintBusy}
              className="bg-teal-600 text-white hover:bg-teal-700">
              {rp?.status === "running" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wrench className="mr-2 h-4 w-4" />}
              {rp?.status === "running" ? "Consertando…" : "Consertar links agora"}
            </Button>
            {rp?.status === "running" && (
              <Button size="sm" variant="outline" data-testid="repair-stop" onClick={stopRepair}
                className="border-red-300 text-red-600 hover:bg-red-50"><Square className="mr-1.5 h-3.5 w-3.5" /> Parar</Button>
            )}
          </div>
          {rp && rp.status !== "idle" && (
            <div className="mt-3 text-xs" data-testid="repair-status">
              <div className="mb-1 flex justify-between text-teal-800">
                <span>🔧 {rp.repaired || 0} consertado(s) · 🔴 {rp.offline || 0} offline · {rp.scanned || 0}/{rp.total || 0}</span>
                <span>{rp.percent || 0}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-teal-200">
                <div className="h-full rounded-full bg-teal-600 transition-all" style={{ width: `${rp.percent || 0}%` }} />
              </div>
              {rp.error && <p className="mt-1 text-red-600">{rp.error}</p>}
              {rp.status === "done" ? <p className="mt-1 font-semibold text-green-700">✅ {(rp.log || []).slice(-1)[0]}</p>
                : (rp.log || []).slice(-1)[0] && <p className="mt-1 text-teal-600">{(rp.log || []).slice(-1)[0]}</p>}
            </div>
          )}
        </div>
      </section>

      {/* Filmes */}
      {s.scope !== "series" && (
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="sync-movies">
        <div className="flex items-center gap-2 bg-amber-500 px-5 py-3 font-display text-lg font-bold text-white"><Film className="h-5 w-5" /> Filmes</div>
        <div className="p-5">
          <p className="font-display text-lg font-bold text-slate-800" data-testid="movies-progress">Processado {s.movies.processed.toLocaleString("pt-BR")} de {s.movies.total.toLocaleString("pt-BR")}</p>
          <p className="text-xs text-slate-400" data-testid="movies-eta">⏱️ Tempo estimado: {fmtEta(s.movies.eta)} · Restam {Math.max(0, s.movies.total - s.movies.processed).toLocaleString("pt-BR")}</p>
          {!done && !stopped && s.movies.current && (
            <p className="mt-2 flex items-center gap-1.5 truncate rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800" data-testid="movies-current">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> <span className="truncate">{s.movies.current}</span>
            </p>
          )}
          <div className="mt-2"><Progress p={s.movies.processed} t={s.movies.total} /></div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Stat label="Inseridos" value={s.movies.inserted} color="text-green-600" />
            <Stat label="Já existiam" value={s.movies.existing} color="text-slate-600" />
            <Stat label="Erros" value={s.movies.errors} color="text-red-500" />
          </div>
          <LogBox lines={s.movies.log} />
        </div>
      </section>
      )}

      {/* Séries */}
      {s.scope !== "movies" && (
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="sync-series">
        <div className="flex items-center gap-2 bg-slate-700 px-5 py-3 font-display text-lg font-bold text-white"><Clapperboard className="h-5 w-5" /> Séries</div>
        <div className="p-5">
          <p className="font-display text-lg font-bold text-slate-800" data-testid="series-progress">Processado {s.series.processed.toLocaleString("pt-BR")} de {s.series.total.toLocaleString("pt-BR")}</p>
          <p className="text-xs text-slate-400" data-testid="series-eta">⏱️ Tempo estimado: {fmtEta(s.series.eta)} · Restam {Math.max(0, s.series.total - s.series.processed).toLocaleString("pt-BR")}</p>
          {!done && !stopped && s.series.current && (
            <p className="mt-2 flex items-center gap-1.5 truncate rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700" data-testid="series-current">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> <span className="truncate">{s.series.current}</span>
            </p>
          )}
          <div className="mt-2"><Progress p={s.series.processed} t={s.series.total} /></div>
          <div className="mt-3 grid grid-cols-4 gap-2">
            <Stat label="Séries novas" value={s.series.inserted} color="text-green-600" />
            <Stat label="Episódios" value={s.series.episodes} color="text-emerald-600" />
            <Stat label="Já existiam" value={s.series.existing} color="text-slate-600" />
            <Stat label="Erros" value={s.series.errors} color="text-red-500" />
          </div>
          <LogBox lines={s.series.log} />
        </div>
      </section>
      )}

      {/* Conteúdo Offline (verificador) */}
      {(s.offline_list || []).length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-rose-200 bg-white" data-testid="offline-section">
          <div className="flex items-center justify-between gap-2 bg-rose-600 px-5 py-3 text-white">
            <span className="flex items-center gap-2 font-display text-lg font-bold"><AlertTriangle className="h-5 w-5" /> Conteúdo Offline ({(s.offline_list || []).length})</span>
            <div className="flex items-center gap-2">
              {off?.status === "running" && (
                <Button size="sm" variant="outline" data-testid="offline-stop" onClick={stopOffline}
                  className="border-white/40 bg-transparent text-white hover:bg-white/20"><Square className="mr-1.5 h-3.5 w-3.5" /> Parar</Button>
              )}
              <Button size="sm" data-testid="offline-delete-all" disabled={syncBusy || maintBusy}
                onClick={startDeleteAll}
                className="bg-white/20 text-white hover:bg-white/30">
                {off?.status === "running" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />} {off?.status === "running" ? "Apagando…" : "Apagar todos"}
              </Button>
            </div>
          </div>
          {off && off.status !== "idle" && (
            <div className="px-5 pt-3 text-xs" data-testid="offline-progress">
              <div className="mb-1 flex justify-between text-rose-800"><span>🗑️ {off.removed || 0} apagado(s) · {off.scanned || 0}/{off.total || 0}</span><span>{off.percent || 0}%</span></div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-rose-200"><div className="h-full rounded-full bg-rose-600 transition-all" style={{ width: `${off.percent || 0}%` }} /></div>
              {off.error && <p className="mt-1 text-red-600">{off.error}</p>}
              {off.status === "done" && <p className="mt-1 font-semibold text-green-700">✅ {(off.log || []).slice(-1)[0]}</p>}
            </div>
          )}
          <div className="p-4">
            <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
              Estes itens estavam <b>offline em todos os fornecedores</b> e <b>não foram subidos</b>. Nada é removido do seu painel automaticamente. Se quiser, apague as <b>cópias antigas</b> que ainda estejam no servidor com o link quebrado.
            </p>
            <div className="max-h-72 space-y-1.5 overflow-auto">
              {(s.offline_list || []).map((o, i) => (
                <div key={i} data-testid={`offline-item-${i}`} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">{o.type === "episode" ? "🎞️" : "🎬"} {o.name}</div>
                    <div className="text-[11px] text-slate-500">{o.cat} · motivo: {o.reason} ({o.code})</div>
                    <div className="truncate font-mono-code text-[10px] text-slate-400">{o.url}</div>
                  </div>
                  <Button size="icon" variant="outline" data-testid={`offline-del-${i}`} disabled={delOff || syncBusy || maintBusy}
                    onClick={() => deleteOffline({ urls: [o.url] }, "Cópia antiga apagada")}
                    className="shrink-0 border-red-300 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Histórico */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-slate-100 px-5 py-3 font-display font-bold text-slate-700"><History className="h-5 w-5" /> Registros Históricos</div>
        <div className="p-4 text-sm">
          <p className="mb-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-700">Apenas os últimos 3 registros são mantidos.</p>
          {(s.history || []).map((h, i) => (
            <div key={i} className="border-b border-slate-100 py-2.5" data-testid={`hist-${i}`}>
              <div className="flex items-center justify-between text-slate-700">
                <span className="font-medium">{new Date(h.at).toLocaleString("pt-BR")}</span>
                <span className="flex items-center gap-1.5 text-[11px]">
                  {h.auto && <span className="rounded bg-purple-100 px-1.5 py-0.5 font-semibold text-purple-700">AUTO</span>}
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${h.summary ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {h.summary ? "Concluído" : "Em andamento"}
                  </span>
                </span>
              </div>
              {h.summary && (
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
                  <span>🎬 {h.summary.inserted} filmes</span>
                  <span>📺 {h.summary.series} séries</span>
                  <span>🎞️ {h.summary.episodes} episódios</span>
                  {h.summary.errors > 0 && <span className="text-red-500">❌ {h.summary.errors} erros</span>}
                </div>
              )}
            </div>
          ))}
          {!(s.history || []).length && <span className="text-slate-400">Nenhum registro ainda.</span>}
        </div>
      </section>
    </motion.div>
  );
}
